// Tone presets for the quote email preview dialog. Each preset takes
// the `previewData` returned by `POST /quotes/{id}/email-preview` and
// produces a full set of overrides — subject, greeting_line, body,
// button_label, closer — so a single click rewrites everything in the
// admin's chosen voice. Callers still get to tweak individual fields
// afterwards; presets are just a starting point, not a lock.
//
// Rules of thumb:
//   - Body copy is plain text with blank lines between paragraphs
//     (the server splits on `\n\n`).
//   - Never hardcode a quote id / amount / expiry in the text — pull
//     them from `previewData` so presets stay accurate as the quote
//     evolves.
//   - Greeting stays a real sentence (no template placeholders); if
//     `previewData.defaultGreeting` gives us a first name we reuse it.

const firstName = (previewData) => {
  const g = (previewData?.defaultGreeting || '').trim();
  // "Hi Alex," -> "Alex"
  const m = /^Hi\s+([^,]+),/i.exec(g);
  return (m ? m[1] : (previewData?.customerName || 'there')).trim();
};

const quoteRef = (previewData) => (previewData?.quoteId || 'the attached quote').trim();

/**
 * Friendly — warm, conversational, first-name basis. Good for
 * repeat customers or ones the admin has an existing relationship
 * with. Keeps things low-pressure ("no rush, no worries").
 */
const friendly = (previewData) => ({
  subject: `Quick one — quote ${quoteRef(previewData)} coming your way`,
  greeting_line: `Hey ${firstName(previewData)},`,
  body_text: [
    `Really appreciate you reaching out — I've put together a quote for the work we discussed. It's attached as a PDF with all the numbers broken down.`,
    `Happy to jump on a quick call if anything needs unpacking, or tweak the scope if the budget's off. No rush, no worries.`,
    `When you're ready, tap Accept below and I'll get it in the diary.`,
  ].join('\n\n'),
  button_label: 'Sounds good — accept',
  closer: 'Cheers',
});

/**
 * Formal — corporate British English, "Dear ...", surname-friendly.
 * Good for procurement teams or first-time customers where the
 * admin doesn't yet know the recipient personally.
 */
const formal = (previewData) => ({
  subject: `Lutrom Engineering — Quotation ${quoteRef(previewData)} for your review`,
  greeting_line: `Dear ${firstName(previewData)},`,
  body_text: [
    `Please find attached our formal quotation ${quoteRef(previewData)} for the works discussed. The document details the scope, itemised costs, and validity period for your review.`,
    `Should you require any clarification or wish to discuss the proposal in further detail, please do not hesitate to contact us.`,
    `To proceed, please accept the quotation using the button below, or reply to this email confirming your acceptance.`,
  ].join('\n\n'),
  button_label: 'Accept quotation',
  closer: 'Kind regards',
});

/**
 * Follow-up — gentle nudge referencing a previous send. Assumes the
 * customer has already seen the quote and hasn't decided yet.
 * Keeps the ask light and offers a way out ("if anything's changed
 * please let us know").
 */
const followUp = (previewData) => ({
  subject: `Following up — quote ${quoteRef(previewData)}`,
  greeting_line: `Hi ${firstName(previewData)},`,
  body_text: [
    `Just circling back on the quote I sent through recently — wanted to check whether you've had a chance to review it.`,
    `If the scope or timing has changed on your end, let me know and I'll gladly revise. Otherwise, tap Accept below and we'll get it booked in.`,
    `As always, happy to talk it through on a call if that's easier.`,
  ].join('\n\n'),
  button_label: 'Accept and book in',
  closer: 'Kind regards',
});

export const QUOTE_TONE_PRESETS = [
  { id: 'friendly', label: 'Friendly', build: friendly },
  { id: 'formal', label: 'Formal', build: formal },
  { id: 'follow-up', label: 'Follow-up', build: followUp },
];
